<script lang="ts" setup>
import * as yup from 'yup'
import { useToast } from 'vue-toastification'

const { $t } = useI18n()

withDefaults(defineProps<ContactProps>(), {
  instructions: '`components.contactForm.instructions`',
})

const { emailValidation, phoneValidation } = useValidations()

const { platform } = useAppConfig()

interface ContactProps {
  instructions?: string
}

const toast = useToast()

const validationSchema = yup.object({
  name: yup.string().required($t('components.contactForm.nameRequired') as string | undefined).min(3, $t('components.contactForm.nameMin') as string | undefined),
  customerEmail: emailValidation,
  phoneNumber: phoneValidation,
  message: yup.string().required($t('components.contactForm.messageRequired') as string | undefined),
  agree: yup.boolean().oneOf([true], $t('components.contactForm.agreeRequired') as string | undefined).required(),
})

const { meta, resetForm, handleSubmit, isSubmitting } = useForm({
  validationSchema,
})

const onSubmit = handleSubmit(async values => {
  try {
    await useFetch('/api/form/contact', {
      method: 'POST',
      body: {
        name: values.name,
        customerEmail: values.customerEmail,
        phoneNumber: values.phoneNumber,
        message: values.message,
      },
    })
    toast.success($t('components.contactForm.successMessage') as string | undefined)
    resetForm()
  }
  catch (e) {
    sentryCaptureException(e)
    toast.error($t('components.contactForm.errorMessage') as string | undefined)
  }
})
</script>

<template>
  <div relative>
    <div absolute h-856px w-full flex justify-center gradient-primary>
      <div relative w-full h-full>
        <NuxtImg
          absolute max-w-524px top="22%" left="80%" w-80px md:top="30%" md:left="50%" md="w-full z-0"
          lg:left="42%"
          self-center z-30 :src="`${'/img/bag.webp'}`"
        />
      </div>
    </div>
    <div fubex-container z-3 relative pt-80px>
      <div max-w-550px flex flex-col gap-14px mb-32px>
        <h1 heading1 font-800 contactForm-text-color>
          {{ $t('components.contactForm.freeConsultation') }}
        </h1>
        <h3 paragraph-md contactForm-text-color>
          {{ $t(`${instructions}`) }}<br>{{ $t('components.contactForm.leaveContact') }}
        </h3>
      </div>
      <div :data-test="getCypressId('contactForm')" mb-72px px-32px py-48px fubex-rounded-lg fubex-shadow-lg-down max-w-640px bg-neutral-100>
        <h2 heading5 font-800 text-neutral-900 mb-60px text-center>
          {{ $t('components.contactForm.formTitle') }}
        </h2>
        <div>
          <form @submit="onSubmit">
            <div flex flex-col gap-20px>
              <FInputVee
                name="name"
                type="text"
                :label="String($t('components.contactForm.nameLabel'))"
                :is-required="true"
              />
              <FInputVee
                name="customerEmail"
                type="text"
                :label="String($t('components.contactForm.emailLabel'))"
                :is-required="true"
              />
              <FInputVee
                name="phoneNumber"
                type="text"
                :label="String($t('components.contactForm.phoneLabel'))"
                :is-required="true"
              />
              <FTextareaVee
                name="message"
                :label="String($t('components.contactForm.messageLabel'))"
                :is-required="true"
              />

              <FCheckboxVee
                name="agree"
                my-16px
              >
                {{ $t('components.contactForm.agreeRequired') }} <NuxtLink :to="`${platform.domain}/docs/gdpr.pdf`" target="_blank" basic-link tabindex="-1" @click.stop>
                  {{ $t('components.contactForm.agreeText') }}
                </NuxtLink>.
              </FCheckboxVee>
              <div flex paragraph-md gap-8px items-center font-500 text-neutral-500>
                <FButton name="contact-send" :disabled="!meta.valid" :loading="isSubmitting" size="lg" w-full type="submit">
                  {{ $t('components.contactForm.sendButton') }}
                </FButton>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
